import { PublicClientApplication, BrowserPerformanceClient, PerformanceEvent, EventType, EventMessage, AccountInfo } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import { fetchUserInfo } from '@/state/slice/userSlice';
import store from '@/state';
import { logFirebaseLogin } from '@/helpers/firebase-helpers';
import { LocalStorageKey, SessionStorageKey } from '@/constants/storage-keys.constants';
import { UserType } from '@/state/interface';
import { initFirebase } from '@/FireBase';
import { ENV } from '@/constants/common.constants';
import { ERROR_MESSSAGES } from '@constants/common.constants';
import { clearReduxStorage } from '@helpers/auth.helper';
import { initApplicationInsights } from '@/AppInsights';

let msalInstancePromise: Promise<PublicClientApplication>;

export async function getMsalInstance(): Promise<PublicClientApplication> {
	if (!msalInstancePromise) {
		msalInstancePromise = (async () => {
			const config = await msalConfig();
			const appConfig = JSON.parse(localStorage.getItem(LocalStorageKey.CONFIG_KEY) || '{}');
			const msalInstance = new PublicClientApplication({
				...config,
				telemetry: {
					client: new BrowserPerformanceClient(config),
				},
			});
			await msalInstance.initialize();

			// Handle redirect promise - this processes redirect responses
			const redirectResponse = await msalInstance.handleRedirectPromise();

			if (appConfig.ENV !== ENV.LOCAL) {
				try {
					initApplicationInsights(appConfig.AI_CONNECTION_STRING);
					initFirebase(appConfig.FIREBASE_CONFIG);
				} catch (error) {
					console.error(ERROR_MESSSAGES.INIT_FIREBASE_FAILED, error);
				}
			}

			const accounts = msalInstance.getAllAccounts();
			if (accounts.length > 1) {
				await msalInstance.clearCache();

				if (redirectResponse?.account) {
					msalInstance.setActiveAccount(redirectResponse.account);
				} else {
					await msalInstance.loginRedirect();
				}
				return msalInstance;
			}

			if (!msalInstance.getActiveAccount()) {
				if (redirectResponse?.account) {
					msalInstance.setActiveAccount(redirectResponse.account);
				} else if (accounts.length === 1) {
					msalInstance.setActiveAccount(accounts[0]);
				}
			}

			// Optional - This will update account state if a user signs in from another tab or window
			msalInstance.enableAccountStorageEvents();

			msalInstance.addPerformanceCallback((events: PerformanceEvent[]) => {
				events.forEach((event) => {
					console.log(event);
				});
			});

			msalInstance.addEventCallback(async (event: EventMessage) => {
				console.log('MSAL Event:', event.eventType);
				switch (event.eventType) {
					case EventType.LOGIN_SUCCESS:
						{
							await clearReduxStorage();
							const account = event.payload as AccountInfo;
							msalInstance.setActiveAccount(account);

							const activeAccount = msalInstance.getActiveAccount();
							if (!activeAccount) break;

							const userType = activeAccount.idTokenClaims?.extension_entity_type as UserType;
							const id = activeAccount.idTokenClaims?.extension_entity_id as string;

							try {
								await store.dispatch(fetchUserInfo({ userType, id }));
								const user = store.getState().userState.user;
								logFirebaseLogin({
									brand: user?.merchant?.brand || '',
									outlet: user?.outletName || user?.name || '',
								});
							} catch (err) {
								console.error('Failed to fetch user info or log Firebase:', err);
							}

							window.localStorage.removeItem(LocalStorageKey.REGISTRATION_ID);
							window.sessionStorage.removeItem(SessionStorageKey.HIDE_INSTALL);
							window.sessionStorage.removeItem(SessionStorageKey.HIDE_NOTI_PROMPT);
						}
						break;
					case EventType.ACQUIRE_TOKEN_SUCCESS:
					case EventType.SSO_SILENT_SUCCESS:
						const account = event.payload as AccountInfo;
						msalInstance.setActiveAccount(account);
						break;

					case EventType.LOGIN_FAILURE: {
						const errorMessage = (event.error as any)?.errorMessage;
						if (errorMessage && (errorMessage.includes('AADB2C90091') || errorMessage.includes('AADB2C90129'))) {
							await msalInstance.loginRedirect();
						}
						break;
					}

					case EventType.ACQUIRE_TOKEN_FAILURE:
					case EventType.SSO_SILENT_FAILURE: {
						const errorMessage = (event.error as any)?.errorMessage;
						if (errorMessage && (errorMessage.includes('AADB2C90091') || errorMessage.includes('AADB2C90129'))) {
							await msalInstance.loginRedirect();
						}
						break;
					}
				}
			});
			return msalInstance;
		})();
	}
	return msalInstancePromise;
}
